import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDisplayPrice } from "@majourneys/currency";
import type { AiCompareLayout, AiCompareRowKey, AiLayoutPlaceRef } from "@majourneys/shared";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { panelStyle } from "../../../shared/ui/card-frame";
import type { ChatLang } from "../hooks/use-ai-chat";
import {
  actionLabelKey,
  FreeToReserve,
  isRestaurantRef,
  LayoutButton,
  LayoutLabel,
  placeHref,
} from "./layout-parts";
import { NumberPin } from "./number-pin";
import { SOFT } from "./soft-tokens";

const ROW_KEY: Record<AiCompareRowKey, string> = {
  setting: "ai_concierge.compare_setting",
  cuisine: "ai_concierge.compare_cuisine",
  type: "ai_concierge.compare_type",
  when: "ai_concierge.compare_when",
  best_for: "ai_concierge.compare_best_for",
};

/** The winner's column: a whisper of gold, not a highlight. */
const WINNER_TINT = "rgba(229,178,101,0.10)";

/**
 * Chat kit C8, Compare: two or three named places side by side in one framed
 * card, each column headed by its number, hairline rows, our verdict, then
 * one button per place.
 *
 * Every row the API sends is filled for every place, so there is no empty
 * cell to explain. The money row follows the payment rules rather than the
 * raw figures: a restaurant says "Free to reserve" and never a price, an
 * explore place says it is visited on your own, and only an activity we sell
 * shows "From". When any cell would be blank the row is left out.
 *
 * On a narrow screen the table scrolls sideways inside its own card, and the
 * page never does.
 */
export function CompareView({ layout, lang }: { layout: AiCompareLayout; lang: ChatLang }) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const { places, rows, pick, pricesMad } = layout;
  const winner = pick && places.some((p) => p.slug === pick.slug) ? pick.slug : undefined;
  const tint = (slug: string) => (slug === winner ? { background: WINNER_TINT } : undefined);

  const priceCell = (p: AiLayoutPlaceRef, i: number): ReactNode | null => {
    if (p.mode === "explore") {
      return (
        <span style={{ color: SOFT.body }}>
          {t("ai_concierge.visit_on_your_own", { defaultValue: "Visit on your own" })}
        </span>
      );
    }
    if (isRestaurantRef(p)) {
      return <FreeToReserve label={t("ai_concierge.free_to_reserve", { defaultValue: "Free to reserve" })} />;
    }
    const price = pricesMad && pricesMad.length === places.length ? pricesMad[i] : undefined;
    if (price == null || !(price > 0)) return null;
    return <span className="font-bold">{t("ai_concierge.place_from", { price: displayPrice(price) })}</span>;
  };
  const priceCells = places.map(priceCell);
  // A row is all or nothing: one place with no honest money line drops it.
  const showPriceRow = priceCells.every((c) => c != null);

  return (
    <section
      style={{ ...panelStyle(18, "#fff"), overflow: "hidden" }}
      aria-label={t("ai_concierge.compare_label")}
      data-testid="compare-card"
    >
      <div className="overflow-x-auto" data-testid="compare-scroll">
        <table className="w-full min-w-[440px] border-collapse text-[12.5px]">
          <thead>
            <tr>
              <th scope="col" className="w-[92px] p-3">
                <span className="sr-only">{t("ai_concierge.compare_label")}</span>
              </th>
              {places.map((p, i) => (
                <th
                  key={p.slug}
                  scope="col"
                  className="p-3 text-start align-bottom"
                  style={tint(p.slug)}
                  data-winner={p.slug === winner ? "true" : undefined}
                >
                  {p.slug === winner ? (
                    <span
                      className="mb-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[1px]"
                      style={{ color: SOFT.goldInk, boxShadow: "0 0 0 1px rgba(138,100,37,0.35)" }}
                    >
                      {t("ai_concierge.compare_our_pick")}
                    </span>
                  ) : null}
                  <span className="flex items-center gap-2">
                    <span className="relative block h-10 w-10 shrink-0">
                      <span
                        className="block h-full w-full overflow-hidden rounded-[10px]"
                        style={{ background: "#eceaf5" }}
                      >
                        {p.heroImageUrl ? (
                          <ImageWithFallback
                            src={p.heroImageUrl}
                            alt=""
                            width={40}
                            height={40}
                            sizes="40px"
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                      </span>
                      <span className="absolute -start-1.5 -top-1.5 scale-[0.85]">
                        <NumberPin index={i} />
                      </span>
                    </span>
                    <span className="text-[13.5px] font-bold leading-[18px]" style={{ color: SOFT.ink }}>
                      {p.title}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} style={{ borderTop: `1px solid ${SOFT.hairline}` }}>
                <RowHead label={t(ROW_KEY[row.key])} />
                {row.values.map((value, i) => (
                  <td
                    key={places[i]?.slug ?? i}
                    className="p-3 text-start align-top leading-[18px]"
                    style={{ color: SOFT.ink, ...tint(places[i]?.slug ?? "") }}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
            {showPriceRow ? (
              <tr style={{ borderTop: `1px solid ${SOFT.hairline}` }}>
                <RowHead label={t("ai_concierge.compare_price")} />
                {priceCells.map((cell, i) => (
                  <td
                    key={places[i]?.slug ?? i}
                    className="p-3 text-start align-top leading-[18px]"
                    style={{ color: SOFT.ink, ...tint(places[i]?.slug ?? "") }}
                    data-testid={`compare-price-${places[i]?.slug}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {pick && winner ? (
        <div className="p-4" style={{ borderTop: `1px solid ${SOFT.hairline}` }} data-testid="compare-verdict">
          <LayoutLabel>{t("ai_concierge.compare_our_pick")}</LayoutLabel>
          <p className="mt-1 text-start text-[13.5px] leading-[20px]" style={{ color: SOFT.ink }}>
            {pick.reason}
          </p>
        </div>
      ) : null}

      <div
        className="flex flex-col gap-2 p-4 sm:flex-row sm:flex-wrap"
        style={{ borderTop: `1px solid ${SOFT.hairline}` }}
      >
        {places.map((p) => {
          const action = t(actionLabelKey(p.action));
          return (
            <LayoutButton
              key={p.slug}
              href={placeHref(p, lang)}
              primary={p.slug === winner}
              ariaLabel={`${action}, ${p.title}`}
            >
              {action} · {p.title}
            </LayoutButton>
          );
        })}
      </div>
    </section>
  );
}

function RowHead({ label }: { label: string }) {
  return (
    <th
      scope="row"
      className="p-3 text-start align-top text-[11px] font-semibold uppercase tracking-[0.06em]"
      style={{ color: SOFT.body }}
    >
      {label}
    </th>
  );
}
